/**
 * A regional fee type, as returned by `POST /v1/fees/types/regional` and stored
 * by the `fees` reducer.
 */
type FeeType = {
  category?: string;
  is_subregion?: boolean;
  name?: string;
  /** Which region levies this fee — the join key onto `regions`. */
  region_id?: string;
};

/**
 * One region the fees API prices. A country it breaks down by province has one
 * region per province; one it does not — Great Britain — has a single region with
 * no `state` at all.
 */
type FeeRegion = {
  _id: string;
  country?: string;
  state?: string;
};

type FeesState = {
  fees: {
    error?: string;
    feeTypes?: FeeType[];
    hasFetched?: boolean;
    isFetching?: boolean;
    regions?: FeeRegion[];
  };
};

/**
 * Stable references so `useSelector` doesn't re-render on every store update, and
 * so a fetch that comes back with no data doesn't look like a change either.
 */
const EMPTY_FEE_TYPES: FeeType[] = [];
const EMPTY_FEE_REGIONS: FeeRegion[] = [];

const selectFeeTypes = (state: FeesState) =>
  state.fees?.feeTypes ?? EMPTY_FEE_TYPES;

const selectFeeRegions = (state: FeesState) =>
  state.fees?.regions ?? EMPTY_FEE_REGIONS;

const selectFeesHaveFetched = (state: FeesState) =>
  Boolean(state.fees?.hasFetched);

const selectFeesAreFetching = (state: FeesState) =>
  Boolean(state.fees?.isFetching);

export {
  EMPTY_FEE_REGIONS,
  EMPTY_FEE_TYPES,
  selectFeeRegions,
  selectFeesAreFetching,
  selectFeesHaveFetched,
  selectFeeTypes,
};
export type { FeeRegion, FeesState, FeeType };
